import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Dumbbell, HeartPulse, Apple, Flower2, Flame, Building2, Target, Music, Swords } from "lucide-react";
import { 
  getCategoryProgramName, 
  getCategoryFunnelPath, 
  getCategoryProgramType,
  getProgramBranding,
  getProgramEmoji,
  getProgramLogoUrl
} from "@/config/programBranding";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Dumbbell,
  HeartPulse,
  Apple,
  Flower2,
  Flame,
  Building2,
  Target,
  Music,
  Swords,
};

interface ProgramCTAProps {
  categoryName?: string;
  variant?: "sidebar" | "inline";
}

export const ProgramCTA = ({ categoryName = "", variant = "sidebar" }: ProgramCTAProps) => {
  const programName = getCategoryProgramName(categoryName);
  const funnelPath = getCategoryFunnelPath(categoryName);
  const programType = getCategoryProgramType(categoryName);
  const branding = getProgramBranding(programType);
  const emoji = getProgramEmoji(programType);
  const logoUrl = getProgramLogoUrl(programType);
  const IconComponent = branding?.icon ? iconMap[branding.icon] : Target;

  const getProfessionalLabel = () => {
    switch (programType) {
      case 'trainer': return 'Entrenador Personal';
      case 'nutritionist': return 'Nutricionista';
      case 'physiotherapist': return 'Fisioterapeuta';
      case 'yoga': return 'Instructor de Yoga';
      case 'crossfit': return 'Coach de CrossFit';
      case 'gym': return 'Propietario de Gimnasio';
      case 'pilates': return 'Instructor de Pilates';
      case 'dance': return 'Profesor de Baile';
      case 'martial_arts': return 'Instructor de Artes Marciales';
      default: return 'Profesional del Fitness';
    }
  };

  if (variant === "inline") {
    return (
      <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt={programName} className="w-[125px] h-auto" />
          ) : (
            <div className="p-2 rounded-lg bg-primary/10">
              <IconComponent className="w-5 h-5 text-primary" />
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-medium">
              ¿Eres {getProfessionalLabel()}? Únete a <strong>{programName}</strong>
            </p>
          </div>
          <Link to={funnelPath}>
            <Button size="sm" variant="default">
              90 días gratis
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden border-2 border-primary/20 hover:border-primary/40 transition-colors">
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-4 py-2">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt={programName} className="h-10 w-auto" />
          ) : (
            <IconComponent className="w-4 h-4" />
          )}
          <span className="font-semibold text-sm">{programName}</span>
        </div>
      </div>
      <CardContent className="p-4">
        {logoUrl && (
          <div className="flex justify-center mb-3">
            <img src={logoUrl} alt={programName} className="w-[150px] h-auto" />
          </div>
        )}
        <h3 className="font-bold mb-2">¿Eres {getProfessionalLabel()}?</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Únete a <strong>{programName}</strong> y empieza a captar clientes con nuestra plataforma todo-en-uno.
        </p>
        <div className="space-y-2">
          <Badge variant="secondary" className="text-xs">
            ✓ 90 días de prueba gratis
          </Badge>
          <Badge variant="secondary" className="text-xs ml-2">
            ✓ Sistema de reservas incluido
          </Badge>
        </div>
        <Link to={funnelPath} className="block mt-4">
          <Button className="w-full" size="sm">
            Unirme a {programName}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};